import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Briefcase, Calendar, ClipboardList, Puzzle, User, Users } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { EntitySearchPopover } from "@/components/calendar/EntitySearchPopover";
import { useCandidates } from "@/hooks/candidates/useCandidates";
import { useCompanyOptions, useJobOptions, useMatchOptions } from "@/hooks/transcripts/useEntityOptions";
import {
	useLinkCandidate,
	useLinkEntity,
	useUnlinkCandidate,
	useUnlinkEntity,
} from "@/hooks/transcripts/useTranscriptMutations";
import { formatFullDateTime } from "@/components/inbox/utils";
import type { Transcript, TranscriptEntityType } from "@/types/transcript";

export interface TranscriptContextPanelProps {
	transcript: Transcript;
}

interface EntityItem {
	id: string;
	name: string;
}

const SEARCH_RESULT_LIMIT = 5;

function searchItems(items: EntityItem[], query: string): EntityItem[] {
	if (!query.trim())
		return [];
	const words = query.toLowerCase().trim().split(/\s+/);
	return items
		.filter((item) => words.every((word) => item.name.toLowerCase().includes(word)))
		.slice(0, SEARCH_RESULT_LIMIT);
}

/**
 * Right pane: meeting participants plus the transcript's links to candidates,
 * matches, jobs and companies. Linked ids come straight from the react-query
 * cache, so the optimistic mutations keep every surface in sync.
 */
export const TranscriptContextPanel: React.FC<TranscriptContextPanelProps> = ({ transcript }) => {
	const { t, i18n } = useTranslation();
	const { candidates: allCandidates } = useCandidates();
	// The panel is mounted only when visible, so the option queries are simply on.
	const matchOptions = useMatchOptions(true);
	const jobOptions = useJobOptions(true);
	const companyOptions = useCompanyOptions(true);

	const linkCandidateMutation = useLinkCandidate();
	const unlinkCandidateMutation = useUnlinkCandidate();
	const linkEntityMutation = useLinkEntity();
	const unlinkEntityMutation = useUnlinkEntity();

	const [candidateQuery, setCandidateQuery] = useState("");
	const [matchQuery, setMatchQuery] = useState("");
	const [jobQuery, setJobQuery] = useState("");
	const [companyQuery, setCompanyQuery] = useState("");

	const candidateItems: EntityItem[] = useMemo(
		() => (allCandidates || []).map((c) => ({ id: c.id, name: c.name || "" })),
		[allCandidates]
	);
	const matchItems: EntityItem[] = useMemo(
		() =>
			(matchOptions.data || []).map((m) => ({
				id: m.id,
				name: `${m.candidate_name || t("transcripts.unknownCandidate", "Unknown candidate")} - ${
					m.job_title || t("transcripts.unknownJob", "Unknown job")
				} (${m.company_name || t("transcripts.unknownCompany", "Unknown company")})`,
			})),
		[matchOptions.data, t]
	);
	const jobItems: EntityItem[] = useMemo(
		() =>
			(jobOptions.data || []).map((j) => ({
				id: j.id,
				name: `${j.title || t("transcripts.unknownJob", "Unknown job")} (${
					j.company_name || t("transcripts.unknownCompany", "Unknown company")
				})`,
			})),
		[jobOptions.data, t]
	);
	const companyItems: EntityItem[] = useMemo(
		() =>
			(companyOptions.data || []).map((c) => ({
				id: c.id,
				name: c.city && c.country ? `${c.name} (${c.city}, ${c.country})` : c.name || t("transcripts.unknownCompany", "Unknown company"),
			})),
		[companyOptions.data, t]
	);

	const linkedCandidateIds = useMemo(
		() => (transcript.linked_candidates || []).map((c) => c.id),
		[transcript.linked_candidates]
	);

	const handleAddCandidate = (item: EntityItem) => {
		setCandidateQuery("");
		linkCandidateMutation.mutate({
			transcriptId: transcript.id,
			candidate: { id: item.id, name: item.name, matched_email: "manual" },
		});
	};

	const handleAddEntity = (entityType: TranscriptEntityType, setQuery: (q: string) => void) => (item: EntityItem) => {
		setQuery("");
		linkEntityMutation.mutate({ transcriptId: transcript.id, entityType, entityId: item.id });
	};

	const handleRemoveEntity = (entityType: TranscriptEntityType) => (id: string) => {
		unlinkEntityMutation.mutate({ transcriptId: transcript.id, entityType, entityId: id });
	};

	const optionsLoading = matchOptions.isLoading || jobOptions.isLoading || companyOptions.isLoading;

	return (
		<div className="flex h-full min-h-0 flex-col bg-background">
			<div className="relative min-h-0 flex-1">
				<div className="absolute inset-0 overflow-y-auto scrollbar-thin">
					<div className="space-y-3 p-4">
						<h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
							{t("transcripts.context.meeting", "Meeting")}
						</h3>
						{transcript.organizer_email && (
							<div className="flex items-center gap-2 text-xs text-muted-foreground">
								<User className="h-3.5 w-3.5 shrink-0" aria-hidden />
								<span className="truncate">{transcript.organizer_email}</span>
							</div>
						)}
						{transcript.start_time && (
							<div className="flex items-center gap-2 text-xs tabular-nums text-muted-foreground">
								<Calendar className="h-3.5 w-3.5 shrink-0" aria-hidden />
								{formatFullDateTime(transcript.start_time, i18n.language)}
							</div>
						)}
						{transcript.participants?.length > 0 && (
							<ul className="space-y-1">
								{transcript.participants.map((participant, index) => (
									<li
										key={`${participant.email || participant.displayName}-${index}`}
										className="truncate text-xs text-muted-foreground"
									>
										{participant.displayName || participant.email}
										{participant.displayName && participant.email && (
											<span className="text-muted-foreground/70"> · {participant.email}</span>
										)}
									</li>
								))}
							</ul>
						)}
					</div>

					<Separator />

					<div className="space-y-4 p-4">
						{optionsLoading ? (
							<div className="space-y-4" aria-hidden>
								{Array.from({ length: 3 }).map((_, index) => (
									<Skeleton key={index} className="h-24 rounded-sm" />
								))}
							</div>
						) : (
							<>
								<EntitySearchPopover
									entityType="candidate"
									searchQuery={candidateQuery}
									onSearchChange={setCandidateQuery}
									searchResults={searchItems(candidateItems, candidateQuery)}
									isSearching={false}
									selectedIds={linkedCandidateIds}
									onAddItem={handleAddCandidate}
									onRemoveItem={(id) =>
										unlinkCandidateMutation.mutate({ transcriptId: transcript.id, candidateId: id })
									}
									placeholder={t("tasks.relations.searchCandidates", "Search candidates...")}
									icon={<Users className="h-3 w-3" />}
									iconBgColor="bg-blue-100"
									iconColor="text-blue-500"
									resultBgColor="bg-blue-50"
									resultHoverColor="hover:bg-blue-100"
									noItemsText={t("transcripts.noCandidates", "No candidates linked")}
									allAvailableItems={candidateItems}
								/>

								<EntitySearchPopover
									entityType="match"
									searchQuery={matchQuery}
									onSearchChange={setMatchQuery}
									searchResults={searchItems(matchItems, matchQuery)}
									isSearching={false}
									selectedIds={transcript.relations?.matches || []}
									onAddItem={handleAddEntity("matches", setMatchQuery)}
									onRemoveItem={handleRemoveEntity("matches")}
									placeholder={t("tasks.relations.searchMatches", "Search matches...")}
									icon={<Puzzle className="h-3 w-3" />}
									iconBgColor="bg-purple-100"
									iconColor="text-purple-600"
									resultBgColor="bg-purple-50"
									resultHoverColor="hover:bg-purple-100"
									noItemsText={t("transcripts.noMatches", "No matches linked")}
									allAvailableItems={matchItems}
								/>

								<EntitySearchPopover
									entityType="job"
									searchQuery={jobQuery}
									onSearchChange={setJobQuery}
									searchResults={searchItems(jobItems, jobQuery)}
									isSearching={false}
									selectedIds={transcript.relations?.jobs || []}
									onAddItem={handleAddEntity("jobs", setJobQuery)}
									onRemoveItem={handleRemoveEntity("jobs")}
									placeholder={t("tasks.relations.searchJobs", "Search jobs...")}
									icon={<ClipboardList className="h-3 w-3" />}
									iconBgColor="bg-green-100"
									iconColor="text-green-600"
									resultBgColor="bg-green-50"
									resultHoverColor="hover:bg-green-100"
									noItemsText={t("transcripts.noJobs", "No jobs linked")}
									allAvailableItems={jobItems}
								/>

								<EntitySearchPopover
									entityType="company"
									searchQuery={companyQuery}
									onSearchChange={setCompanyQuery}
									searchResults={searchItems(companyItems, companyQuery)}
									isSearching={false}
									selectedIds={transcript.relations?.companies || []}
									onAddItem={handleAddEntity("companies", setCompanyQuery)}
									onRemoveItem={handleRemoveEntity("companies")}
									placeholder={t("tasks.relations.searchCompanies", "Search companies...")}
									icon={<Briefcase className="h-3 w-3" />}
									iconBgColor="bg-orange-100"
									iconColor="text-orange-600"
									resultBgColor="bg-orange-50"
									resultHoverColor="hover:bg-orange-100"
									noItemsText={t("transcripts.noCompanies", "No companies linked")}
									allAvailableItems={companyItems}
								/>
							</>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};
